import { Component, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  UntypedFormControl,
  Validators,
} from '@angular/forms';
import {
  AlertController,
  ModalController,
  Platform,
  NavController,
} from '@ionic/angular';
import {
  InventoryCount,
  InventoryCountLineItem,
} from '../../models/product-inventory-count.model';
import { Product } from '../../models';
import { ProductService } from '../../services/product.service';
import { InventoryCountService } from '../../services/product-inventory-counts.service';
import { List } from 'immutable';
import { of, Observable } from 'rxjs';
import { Utilities } from '../../utilities/utilities';
import { Router, ActivatedRoute } from '@angular/router';
import { AlertService } from '../../utilities/alert.service';
import { ProductListComponent } from '../product-list/product-list.component';

interface IRow {
  ID: number;
  productID: number;
  name: string;
  brand: string;
  size: string;
  expectedCount: number;
  actualCount: number;
}

interface IInventoryCountModalCallBackData {
  inventoryCount: InventoryCount;
}

@Component({
  selector: 'app-inventory-count',
  templateUrl: './inventory-count.page.html',
  styleUrls: ['./inventory-count.page.scss'],
})
export class InventoryCountPage implements OnInit {
  public menuCollapsed: boolean = false;
  public isLoading: boolean;

  public submitted: boolean;

  public inventoryCountID: number;

  public products: List<Product>;

  public inventoryCount: InventoryCount;

  public inventoryCountLineItems: List<InventoryCountLineItem>;

  public stats: Array<IRow>;

  public errorOccurredWhileLoadingProducts: boolean = false;

  public errorOccurredWhileLoadingInventoryCountInfo: boolean;

  public countItemID: number;
  public countItemStatusID: UntypedFormControl;

  public countItem: InventoryCount;

  public countItemForm: UntypedFormGroup;

  public countItemName: UntypedFormControl;
  public countItemDate: UntypedFormControl;
  public notes: UntypedFormControl;
  public isFullCount: number = 1;
  public actualCount: UntypedFormControl;
  public saveButtonText: string;
  public isIOS: boolean;

  constructor(
    private productService: ProductService,
    private inventoryCountService: InventoryCountService,
    private alertCtrl: AlertController,
    private formBuilder: UntypedFormBuilder,
    private modalController: ModalController,
    private router: Router,
    private route: ActivatedRoute,
    public platform: Platform,
    private navCtrl: NavController,
    private _alertService: AlertService
  ) {
    // this.countItemID = this.navParams.get('ID');
    // this.saveButtonText = this.navParams.get('saveButtonText') || 'Save';
    // this.inventoryCountID = this.navParams.get('inventoryCountID');

    this.route.queryParams.subscribe((params) => {
      this.inventoryCountID = params['id'];
    });
    this.submitted = false;
    this.isLoading = true;
    this.products = List([]);
    this.inventoryCountLineItems = List([]);
    this.stats = [];
    this.actualCount = new UntypedFormControl();
    this.countItemName = new UntypedFormControl(
      '',
      Validators.compose([Validators.required, Validators.maxLength(128)])
    );
    this.notes = new UntypedFormControl(
      '',
      Validators.compose([Validators.maxLength(128)])
    );
    this.countItemStatusID = new UntypedFormControl(
      '',
      Validators.compose([Validators.maxLength(25)])
    );
    this.countItemDate = new UntypedFormControl();
    this.countItemForm = this.formBuilder.group({
      actualCount: this.actualCount,
      countItemName: this.countItemName,
      countItemDate: this.countItemDate,
      notes: this.notes,
      countItemStatusID: this.countItemStatusID,
      isFullCount: this.isFullCount,
    });
  }

  public ngOnInit() {
    if (this.platform.is('ios')) this.isIOS = true;
    this.loadProducts();
    this.refresh();
  }

  public loadProducts() {
    this.isLoading = true;
    this.errorOccurredWhileLoadingProducts = false;
    // Check if new count or existing
    if (!this.inventoryCountID) {
      // New count - go fetch all products that have inventory
      this.productService.loadProducts().subscribe({
        next: (products) => {
          this.countItemDate.setValue(new Date());
          this.products = products.filter(p => p.trackInventory === 1);         
          this.mapPaginatorValue();
          this.isLoading = false;
        },
        error: (err) => {
          this.errorOccurredWhileLoadingProducts = true;
        },
      });
    } else {
      // let products = this.countItem.getLineItems();
      // Existing count - load the line items from the count
      this.inventoryCountService
        .loadItemByID(this.inventoryCountID)
        .subscribe((val) => {
          this.countItem = val;
          this.countItemName.setValue(val.name);
          this.notes.setValue(val.notes);
          this.isFullCount = val.isFullCount;
          this.countItemStatusID.setValue(val.statusID);
          this.countItemDate.setValue(Utilities.parseDate(val.dateTime));
          val.lineItems.forEach((lineItem) => {
            let ID = lineItem.ID;
            let productID = lineItem.productID;
            this.productService
              .loadProductByID(productID)
              .subscribe((product) => {
                console.log(product)
                let name = product.getName();
                let brand = product.brand?.getName() ? product.brand?.getName() : "";
                let size = product.size;
                let expectedCount = lineItem.expectedCount;
                let actualCount = lineItem.actualCount;
                this.stats.push({
                  ID,
                  name,
                  brand,
                  size,
                  expectedCount,
                  actualCount,
                  productID,
                });
              });
          });
          setTimeout(() => {
            this.isLoading = false;
          }, 500);
        });
    }
  }

  public mapPaginatorValue() {
    this.stats = [];
    this.products.forEach((product) => {
      let ID = undefined;
      let productID = product.getID();
      let name = product.getName();
      let brand = product.brand?.getName();
      let size = product.size;
      let expectedCount = product.getStock();
      let actualCount = undefined;
      this.stats.push({ ID, name, brand, size, expectedCount, actualCount, productID });
    });
  }

  public refresh() {
    this.isLoading = true;
    this.errorOccurredWhileLoadingInventoryCountInfo = false;
    this.loadInventoryCount();
    // .pipe(
    //     switchMap((countItem: InventoryCount) => {
    //         this.countItem = countItem;
    //         this.title = (this.countItem.ID === undefined) ? "Create InventoryCount" : this.inventorycount.name;
    //     })
    // );
  }

  public loadInventoryCount(): Observable<InventoryCount> {
    return of<InventoryCount>(
      new InventoryCount({
        ID: undefined,
        name: undefined,
        dateTime: undefined,
        notes: '',
        statusID: undefined,
        statusName: undefined,
        isFullCount: 1,
        deleted: 0,
        lineItems: undefined,
      })
    );
  }

  public async showProductList() {
    const modal = await this.modalController.create({
      component: ProductListComponent,
    });
    modal.present();
    modal.onDidDismiss().then(({data}) => {
      if (data) {
        data.selectedProducts.forEach((product) => {
          let ID = undefined;
          let productID = product.ID;
          let name = product.name;
          let brand = product.brand?.getName();
          let size = product.size;
          let expectedCount = product.expectedCount;
          let actualCount = undefined;
          if (
            this.stats.findIndex((newItem) => newItem.ID === product.ID) === -1
          ) {
            this.stats.push({
              ID,
              name,
              brand,
              size,
              expectedCount,
              actualCount,
              productID,
            });
          }
        });
      }
    });
  }

  public remove(stat) {
    this.stats.splice(this.stats.indexOf(stat), 1);
  }

  public inventorySave(statusID) {
    if (!this.isFormValid(statusID)) {
      return;
    }

    let inventoryCount = new InventoryCount({
      ID: undefined,
      name: undefined,
      dateTime: undefined,
      notes: undefined,
      statusID: undefined,
      statusName: undefined,
      isFullCount: undefined,
      lineItems: List([]),
      deleted: 0,
    });
    inventoryCount = inventoryCount.setID(this.inventoryCountID);
    inventoryCount = inventoryCount.setDateTime(
      Utilities.formatDate(this.countItemDate.value)
    );
    inventoryCount = inventoryCount.setName(String(this.countItemName.value));
    inventoryCount = inventoryCount.setNotes(String(this.notes.value));
    inventoryCount = inventoryCount.setStatusID(statusID);
    let statusName = statusID == 1 ? 'Completed' : 'Paused';
    inventoryCount = inventoryCount.setStatusName(statusName);
    inventoryCount = inventoryCount.setFullCount(this.isFullCount);
    let lineItems = [];
    this.stats.forEach((stat) => lineItems.push(stat));
    inventoryCount = inventoryCount.setLineItems(List(lineItems));

    this.inventoryCountService.saveInventoryCount(inventoryCount).subscribe({
      next: (inventoryCount) => {
        let callBackData: IInventoryCountModalCallBackData = {
          inventoryCount: inventoryCount,
        };

        this.isLoading = false;
        this.router.navigateByUrl('/inventory-count');
      },
      error: async (error) => {
        this.isLoading = false;
        const alert = await this._alertService.showDefaultError();
        alert.present();
      },
    });
  }

  public onCountTypeChange($event: any) {
    this.isFullCount = parseInt($event.detail.value);
    this.stats = [];
    this.loadProducts();
  }

  public isFormValid(statusID): boolean {
    if (statusID === 1 && !this.countItemForm.valid) {
      alert('please fill in all fields');
      return false;
    }
    return true;
  }

  public async back() {
    if (this.countItemStatusID.value == 1) {
      this.navCtrl.setDirection('back');
      this.router.navigateByUrl('/inventory-count');
    } else {
      const alert = await this.alertCtrl.create({
        header: 'Save?',
        message:
          "You've made changes to your inventory count. Would you like to save the count before leaving the page?",
        buttons: [
          {
            text: 'Save',
            role: 'cancel',
            handler: () => {
              this.inventorySave(2);
            },
          },
          {
            text: "Don't save",
            role: 'confirm',
            cssClass: 'primary-button',
            handler: () => {
              this.navCtrl.setDirection('back');
              this.router.navigateByUrl('/inventory-count');
            },
          },
        ],
      });
      alert.present();
    }
  }
}
